/**
 * ProtocolPanel — the commit–challenge–response protocol, and its two
 * properties (soundness and zero-knowledge) in plain English, then the math.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
export function ProtocolPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Commit–Challenge–Response"
      subtitle="An interactive zero-knowledge proof runs in three beats. The prover commits, the auditor challenges, the prover responds — and the auditor is convinced without ever seeing the witness."
      accentColor={panelTokens.accentBlue}
    >
      <PanelSurface.SectionHeading>The three beats</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ContentList
          ariaLabel="The commit, challenge, response protocol beats"
          variant="ordered"
          items={[
            {
              id: "commit",
              title: "Commit",
              body: "The prover binds itself to a hidden witness with a commitment — a sealed envelope. The commitment leaks nothing about the witness, yet the prover can no longer change it. Here: a commit⟨…⟩ tag derived from the challenge alone, never from a DAG edge.",
              color: panelTokens.commit,
            },
            {
              id: "challenge",
              title: "Challenge",
              body: "The auditor issues a compliance statement: \"subject S may reach authority A under PERMIT.\" The auditor never asks to see the witness — only for a proof that one exists.",
              color: panelTokens.challenge,
            },
            {
              id: "respond",
              title: "Respond",
              body: "The prover answers in a way that an actual witness makes possible and a non-witness makes impossible. A correct response convinces the auditor; it still discloses no element of the witness.",
              color: panelTokens.response,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        The two properties, plainly
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="Zero-knowledge — the auditor learns nothing but the fact"
        tone="info"
        color={panelTokens.witness}
      >
        After the exchange the auditor knows exactly one new thing: that a
        witness exists. It learns nothing about which subjects, which
        authorities, or which path. Formally, a simulator with no access to the
        witness can produce a transcript indistinguishable from the real one —
        so the real transcript cannot carry witness information. In this demo the
        protocol simply has no disclosure step to begin with: the
        reveal-the-witness move is structurally absent from the move set.
      </PanelSurface.Callout>
      <PanelSurface.Callout
        title="Soundness — a non-compliant prover cannot be accepted"
        tone="warn"
        color={panelTokens.violation}
      >
        If the statement is false — no reachability path exists in the DAG — then
        no response convinces the auditor. In standard cryptography this holds
        only with negligible probability, under a hardness assumption. In CSK it
        holds with probability bound <strong>0</strong>:{" "}
        <code className="font-mono">¬ IsCompliant_T x</code> means the witness
        type is empty, so an accepting transcript would require constructing an
        element of an empty type — impossible in any sound formal system. The
        accepting move is <em>absent because there is no witness</em>, not
        rejected after an attempted forgery.
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>
        The math, for those who want it
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`-- A ZKP system for a relation R ⊆ W × X is a triple (P, V, S):
Completeness  :  (w, x) ∈ R   →  V accepts P's transcript
Soundness     :  x ∉ L_R      →  no P convinces V          -- CSK: prob. bound 0
Zero-knowledge:  ∃ S.  S(x) ≈ ⟨P(w) ⇄ V⟩                   -- simulator, no w

-- For CSK, the statement and witness are categorical:
X = GovernanceStatement(T)      -- "this component has PERMIT coverage"
W(T, x)                         -- the actual morphism / lift / token
encode T x                      -- the ZKP statement; its proof needs an element of W(T, x)

-- The soundness lemma (ZKP-3):
ZKP-Verifies (encode T x) π  →  ∥ W(T, x) ∥
-- contrapositive: ¬ ∥ W(T, x) ∥  →  ¬ ∃ π. ZKP-Verifies (encode T x) π`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.Callout
        title="Non-interactive, auditable, composable"
        tone="safe"
        color={panelTokens.immune}
      >
        Applying Fiat–Shamir turns the interactive exchange into a single proof
        string π that any auditor can check against x — a NIZK. Compliance is
        then recorded on an append-only ledger as <code>(x, π)</code> pairs:
        publicly verifiable, persistent, and private. And by ZKP-4, component
        proofs compose through the COMPOSE operad into a proof for the composite
        — no need to reprove the whole system on every change.
      </PanelSurface.Callout>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-25 §2 ZKP semantics in categorical terms",
            path: "research/settled/RD-25-ZERO-KNOWLEDGE-COMPLIANCE-PROOFS.md",
          },
          {
            label: "ComplianceZKP.agda (SoundZKPEncoding, ZKP-3)",
            path: "agda/Research/ZeroKnowledgeCompliance/ComplianceZKP.agda",
          },
          {
            label: "CanonicalSoundness.agda (PERMIT ZKP-3, unconditional)",
            path: "agda/Research/ZeroKnowledgeCompliance/CanonicalSoundness.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
